import { type DataSnapshot, child } from "firebase/database";
import { use, useCallback, useMemo } from "react";

import { CompanyContext } from "@domain/context/CompanyContext";
import {
    refBankTransactionDocumentAssociations,
    refCashRegisterTransactionDocumentAssociations,
    refRecordDocumentAssociations,
} from "@domain/api/firebase/firebaseRootRefs";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { type DmsAssociationsData } from "@domain/context/DmsDataContexts";

const stringArrayInitializer = (snap: DataSnapshot): string[] => {
    const value = snap.val();
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
};

/** Inverts a `Map<originId, docId[]>` into `Map<docId, Set<originId>>`. */
const invertAssociations = (origin: Map<string, string[]>): Map<string, Set<string>> => {
    const result = new Map<string, Set<string>>();
    origin.forEach((docIds, originId) => {
        docIds.forEach((docId) => {
            let set = result.get(docId);
            if (!set) {
                set = new Set();
                result.set(docId, set);
            }
            set.add(originId);
        });
    });
    return result;
};

/**
 * Listens to the three realtime collections under `associations/{companyId}`
 * (records / bank transactions / cash register transactions) and emits the
 * merged DMS-side view. IDs only - full record/transaction content is fetched
 * lazily by the consumers that need it (records via GraphQL, bank + cash
 * register via the still-loaded global contexts for now).
 */
export const useDmsAssociationsData = (): DmsAssociationsData => {
    const { companyGQL } = use(CompanyContext);
    const companyId = companyGQL?.id;
    const shouldSkipLoad = !companyId;

    const recordsRef = useMemo(() => child(refRecordDocumentAssociations, String(companyId)), [companyId]);
    const bankRef = useMemo(() => child(refBankTransactionDocumentAssociations, String(companyId)), [companyId]);
    const kbRef = useMemo(() => child(refCashRegisterTransactionDocumentAssociations, String(companyId)), [companyId]);

    const initializer = useCallback(stringArrayInitializer, []);
    const { state: recordsAssociations } = useCollectionListener<string[]>(recordsRef, initializer, shouldSkipLoad);
    const { state: bankAssociations } = useCollectionListener<string[]>(bankRef, initializer, shouldSkipLoad);
    const { state: kbAssociations } = useCollectionListener<string[]>(kbRef, initializer, shouldSkipLoad);

    const documentAccountingRecords = useMemo(() => invertAssociations(recordsAssociations), [recordsAssociations]);
    const documentBankingTransactions = useMemo(() => invertAssociations(bankAssociations), [bankAssociations]);
    const documentCashRegisterTransactions = useMemo(() => invertAssociations(kbAssociations), [kbAssociations]);

    return useMemo(
        () => ({
            documentAccountingRecords,
            documentBankingTransactions,
            documentCashRegisterTransactions,
        }),
        [documentAccountingRecords, documentBankingTransactions, documentCashRegisterTransactions],
    );
};
